import React, { useCallback } from 'react';
import { Button, Table } from 'semantic-ui-react';
import * as Big from 'big-ts';
import {
  createCryptoBuySellRange,
  CryptoBuySellRange,
} from '../../../../types/models/CryptoBuySellRange';
import CryptoBuySellRangeView from './CryptoBuySellRangeView';
import { createFeeValue } from '../../../../types/models/FeeValue';
import { AmountType } from '../../../../types/enums/AmountType';
import useUpdate from '../../../../hooks/useUpdate';
import { SetState } from '../../../../types/utility';
import useDelete from '../../../../hooks/useDelete';
import useCreate from '../../../../hooks/useCreate';
import { ValidationErrors } from '../../../../types/models/ValidationErrors';
import { toNumber } from '../../../../utils/number';
import { FeeValueType } from 'src/types/enums/FeeValueType';

interface CryptoBuySellRangesProps {
  ranges: CryptoBuySellRange[];
  typeAmounts: keyof typeof AmountType;
  typeRates: keyof typeof FeeValueType;
  setRanges: SetState<CryptoBuySellRange>;
  defaultCurrencyId?: number;
  feeTypes?: FeeValueType[];
  readOnly: boolean;
  validationErrors?: ValidationErrors;
}

export default function CryptoBuySellRanges({
  defaultCurrencyId,
  typeAmounts,
  typeRates,
  ranges,
  setRanges,
  readOnly = false,
  feeTypes,
  validationErrors = {},
}: CryptoBuySellRangesProps): JSX.Element {
  const sortedRanges = ranges
    .slice()
    .sort((a, b) => Number(a.amountFrom) - Number(b.amountFrom));
  const addRange = useCreate<CryptoBuySellRange>(setRanges);
  const updateRange = useUpdate<CryptoBuySellRange>(setRanges);
  const deleteRange = useDelete<CryptoBuySellRange>(setRanges);

  const onAddRange = useCallback(() => {
    let amountFrom;
    if (typeAmounts === AmountType.SINGLE) {
      const greatestAmountFrom = ranges.reduce((acc, range) => {
        const amountFrom = toNumber(range.amountFrom, 0);
        return acc > amountFrom ? acc : amountFrom;
      }, 0);
      amountFrom = Big.toString(Big.parse(greatestAmountFrom));
    } else if (typeAmounts === AmountType.RANGE) {
      const greatestAmountTo = ranges.reduce((acc, range) => {
        const amountTo = toNumber(range.amountTo, 0);
        return acc > amountTo ? acc : amountTo;
      }, 0);
      amountFrom =
        greatestAmountTo < Infinity
          ? Big.toString(
              Big.add(Big.parse(greatestAmountTo))(Big.parse('0.01')),
            )
          : '';
    }
    addRange(
      createCryptoBuySellRange({
        amountFrom,
        fees: [
          createFeeValue({
            type: feeTypes && feeTypes[0],
            currencyId: defaultCurrencyId,
          }),
        ],
      }),
    );
  }, [addRange, ranges, typeAmounts, defaultCurrencyId, feeTypes]);
  return (
    <div className="crypto_form_collect__table_container">
      <Table celled className="crypto_form_collect__table">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Amount</Table.HeaderCell>
            <Table.HeaderCell>
              {typeRates === FeeValueType.FIXED ? 'Rate' : 'Margin %'}
            </Table.HeaderCell>
            <Table.HeaderCell>Fee</Table.HeaderCell>
            {!readOnly && <Table.HeaderCell />}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {sortedRanges.map((range, index) => (
            <CryptoBuySellRangeView
              key={range.id}
              defaultCurrencyId={defaultCurrencyId}
              typeAmounts={typeAmounts}
              feeTypes={feeTypes}
              range={range}
              readOnly={readOnly}
              onChange={updateRange}
              onDelete={deleteRange}
              validationErrors={
                validationErrors[index] as ValidationErrors | undefined
              }
            />
          ))}
        </Table.Body>
      </Table>
      {!readOnly && (
        <div className="crypto_form_collect__table_actions">
          <Button
            data-qa="add-range"
            primary
            onClick={onAddRange}
            type="button"
          >
            Add Range
          </Button>
        </div>
      )}
    </div>
  );
}
